/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import ModalImage from '@components/modal/ModalImage';
import AddImage from '@/screens/sales-activity/components/todo-list/on-going/image/AddImage';
import RemoveImage from '@/screens/sales-activity/components/todo-list/on-going/image/RemoveImage';
import { ButtonSticky } from '@components/screen/Layout';
import { showPopup } from '@/store/modal';
import { showAlert, showImage } from '@/store/global';
import { logEvent } from '@global/analytics';
import Colors from '@global/colors';

const imageSize = 100;
const imageStyle = {
  position: 'relative',
  width: imageSize,
  height: imageSize,
  borderRadius: 4,
  overflow: 'hidden',
};

const MemoImage = React.memo(({ uri = '' }) => {
  return (
    <Image
      style={{ width: imageSize, height: imageSize }}
      width={imageSize}
      height={imageSize}
      source={{ uri }}
      disableCDN={true}
    />
  );
});
MemoImage.displayName = 'MemoImage';

const TodoUploadSharePhoto = ({
  containerHeight = 0,
  detailData = {},
  detailTodo = {},
  localPayload = null,
  onSubmit = () => {},
  onSaveDraft = () => {},
  resetTab = () => {},
}) => {
  const dispatch = useDispatch();
  const isLoadingGlobal = useSelector((state) => state.modal.isLoading);
  const [payloadSharePhoto, setPayloadSharePhoto] = useState([]);
  const [uploading, setUploading] = useState(false);

  const mapAttachmentsToImages = (attachments = []) =>
    attachments?.[0]?.image?.map((a) => ({
      ...a,
      url: a.url,
      text: '',
      uri: a.url,
    })) || [];

  const getImagesSignature = (images = []) =>
    JSON.stringify(
      images.map((item) => ({
        uri: item?.uri || '',
        url: item?.url || '',
        text: item?.text || '',
      })),
    );

  const buildImageEntry = (img) => {
    const url = typeof img === 'string' ? img : img?.url || img?.uri || '';
    const uri = typeof img === 'string' ? img : img?.uri || img?.url || '';
    // B2B-5776: same fix as customer display — reuse the existing _localFile so
    // the draft round-trip cannot drop the filename and mime type
    const localFile = img?._localFile || {
      uri,
      filename: img?.filename,
      type: img?.type,
      mimeType: img?.mimeType,
    };
    return {
      text: '',
      url,
      ...(uri && !uri.startsWith('http') ? { _localFile: localFile } : {}),
    };
  };

  const onSubmitSharePhoto = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const payload = {
        more_info: {
          attachments: [
            {
              type: 'store',
              name: 'upload_store_photo',
              image: payloadSharePhoto.map(buildImageEntry),
            },
          ],
        },
      };
      logEvent({
        eventName: 'submit_activity',
        data: {
          activity_id: detailData?._id,
          task_id: detailTodo?._id,
          type: 'upload_store_photo',
          payload: JSON.stringify(payload),
        },
        dataKey: 'detail',
      });
      await onSubmit({
        targetType: 'upload_store_photo',
        taskId: detailTodo?._id,
        payload,
      });
      dispatch(showPopup({ name: 'isLoading', value: false }));
      resetTab();
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      let error_message = 'A system error occurred';
      if (error?.response?.data?.message) {
        error_message = error.response.data.message;
      }
      if (!detailTodo?._id) {
        error_message = 'This activity have no todo list activity.';
      }
      dispatch(
        showAlert({
          show: true,
          isModal: true,
          title: 'Error',
          description: error_message,
          isConfirm: false,
          testID: 'todo-upload-photo-validation',
        }),
      );
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setUploading(false);
      const localAttachments =
        localPayload?.more_info?.attachments || localPayload?.attachments || [];

      if (localAttachments?.length) {
        const images = mapAttachmentsToImages(localAttachments);
        setPayloadSharePhoto((prev) =>
          getImagesSignature(images) === getImagesSignature(prev)
            ? prev
            : images,
        );
        return;
      }
      if (detailTodo?.more_info?.attachments?.length) {
        const images = mapAttachmentsToImages(detailTodo.more_info.attachments);
        setPayloadSharePhoto((prev) =>
          getImagesSignature(images) === getImagesSignature(prev)
            ? prev
            : images,
        );
      }
    }
    return () => {
      mounted = false;
    };
  }, [detailTodo, localPayload]);

  const saveDraftRef = React.useRef(onSaveDraft);
  const lastSavedSignatureRef = React.useRef('');

  useEffect(() => {
    saveDraftRef.current = onSaveDraft;
  }, [onSaveDraft]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const payloadSignature = getImagesSignature(payloadSharePhoto);
      if (lastSavedSignatureRef.current === payloadSignature) {
        return;
      }

      const payload = {
        more_info: {
          attachments: [
            {
              type: 'store',
              name: 'upload_store_photo',
              image: payloadSharePhoto.map(buildImageEntry),
            },
          ],
        },
      };
      lastSavedSignatureRef.current = payloadSignature;
      saveDraftRef.current({
        targetType: 'upload_store_photo',
        taskId: detailTodo?._id,
        payload,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [payloadSharePhoto, detailTodo?._id]);

  const SharePhoto = useMemo(() => {
    return payloadSharePhoto.length
      ? payloadSharePhoto.map((item, idx) => (
          <View style={[imageStyle, styles.imageItem]} key={idx}>
            <RemoveImage
              index={idx}
              data={payloadSharePhoto}
              setData={(val) => setPayloadSharePhoto(val)}
            />
            <TouchableOpacity
              testID={`todo-upload-photo-view-${idx}`}
              onPress={() => {
                dispatch(
                  showImage({
                    isModal: true,
                    isMultiple: true,
                    allImage: [item.uri],
                    url: item.uri,
                    disableCDN: true,
                    noArrow: true,
                  }),
                );
              }}>
              <MemoImage uri={item.uri} />
            </TouchableOpacity>
          </View>
        ))
      : false;
  }, [imageStyle, imageSize, payloadSharePhoto]);

  return (
    <View testID="todo-upload-photo-container" style={styles.pageContainer}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ width: '100%', height: containerHeight }}
        contentContainerStyle={styles.scrollContent}>
        <View style={styles.introCard}>
          <SociollaText type="bold" style={styles.introTitle}>
            Store Photos
          </SociollaText>
          <SociollaText style={styles.introDescription}>
            Upload photos of the potential customer&apos;s store during your
            visit.
          </SociollaText>
        </View>

        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <View>
              <SociollaText type="medium" style={styles.sectionTitle}>
                Upload Store Photo
              </SociollaText>
              <SociollaText style={styles.sectionCaption}>
                Add up to 5 store photos.
              </SociollaText>
            </View>
            <SociollaText style={styles.sectionCountText}>
              {payloadSharePhoto.length}/5
            </SociollaText>
          </View>

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.imageRowScroll}
            contentContainerStyle={styles.imageRow}>
            {payloadSharePhoto.length < 5 ? (
              <View style={[imageStyle, styles.imageItem]}>
                <AddImage
                  data={payloadSharePhoto}
                  setData={(val) =>
                    setPayloadSharePhoto((prev) => [...prev, val])
                  }
                  disabled={uploading}
                  loadingCallback={(val) => setUploading(val)}
                  additional={{
                    activity: detailData,
                    task: detailTodo,
                    showImageOption: true,
                    isMultipleImage: true,
                    limit: 5,
                  }}
                />
              </View>
            ) : null}
            {SharePhoto}
          </ScrollView>
        </View>
      </ScrollView>
      <ButtonSticky
        show
        customHost="modal-sticky-button"
        buttons={[
          {
            title: uploading ? 'Uploading...' : 'Submit Store Photo',
            disabled:
              !payloadSharePhoto.length || isLoadingGlobal || uploading
                ? true
                : false,
            onPress: () => onSubmitSharePhoto(),
          },
        ]}
      />
      <ModalImage />
    </View>
  );
};

export default React.memo(TodoUploadSharePhoto, (prev, next) => prev == next);

const styles = StyleSheet.create({
  pageContainer: {
    backgroundColor: Colors.grey3,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 100,
  },
  introCard: {
    backgroundColor: Colors.white,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  introTitle: {
    fontSize: 18,
    color: Colors.black3,
    marginBottom: 8,
  },
  introDescription: {
    fontSize: 13,
    color: Colors.grey2,
    lineHeight: 20,
  },
  sectionCard: {
    backgroundColor: Colors.white,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    elevation: 5,
    borderLeftWidth: 5,
    borderLeftColor: Colors.blue4,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  sectionTitle: {
    fontSize: 14,
    color: Colors.black3,
  },
  sectionCaption: {
    fontSize: 11,
    color: Colors.grey2,
    marginTop: 2,
  },
  sectionCountText: {
    fontSize: 12,
    color: Colors.blue1,
  },
  imageRowScroll: {
    marginHorizontal: -2,
  },
  imageRow: {
    paddingHorizontal: 2,
  },
  imageItem: {
    marginRight: 10,
  },
});
